import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axiosConfig";
import ModuleForm from "./ModuleForm";
import Modal from "./Modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Modules() {
  const [modules, setModules] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModule, setEditModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletedModule, setDeletedModule] = useState(null);
  const [undoTimeoutId, setUndoTimeoutId] = useState(null);
  const [isAccessIdModalOpen, setIsAccessIdModalOpen] = useState(false);
  const [currentModule, setCurrentModule] = useState(null);
  const [moduleAccessCode, setModuleAccessCode] = useState("");
  const [featureAccessCodes, setFeatureAccessCodes] = useState([]);
  const [viewMode, setViewMode] = useState("product");
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const productData = localStorage.getItem("selectedProduct");
    if (productData) {
      try {
        setSelectedProduct(JSON.parse(productData));
      } catch (error) {
        console.error("Error parsing selected product:", error);
      }
    }
  }, []);

  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint =
        viewMode === "product" && selectedProduct?.productId
          ? `/modules/${selectedProduct.productId}/products`
          : "/modules";
      const response = await axiosInstance.get(endpoint);
      setModules(Array.isArray(response.data.data) ? response.data.data : []);
    } catch {
      toast.error("Failed to fetch modules. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedProduct]);

  useEffect(() => {
    if (selectedProduct) fetchModules();
  }, [fetchModules, selectedProduct]);

  const handleOpenAccessIdModal = (module) => {
    setCurrentModule(module);
    setModuleAccessCode(module.moduleAccessId || "");
    setFeatureAccessCodes(
      module.moduleFeatureAccessIds.map((item) => ({
        moduleFeatureAccessId: item.moduleFeatureAccessId,
        featureAccessCode: item.featureAccessCode || "",
        featureName: item.featureName || "",
      })),
    );
    setIsAccessIdModalOpen(true);
  };

  const handleUpdateAccessId = async () => {
    if (!moduleAccessCode.trim()) {
      toast.error("Access Code is required.");
      return;
    }
    const data = {
      moduleId: currentModule.moduleId,
      moduleAccessCode,
      featureAccessCodes,
    };

    // If the access code was changed, tell the backend what the old
    // code was so it can be replaced there.
    if (
      currentModule.moduleAccessId &&
      moduleAccessCode !== currentModule.moduleAccessId
    ) {
      data.oldProductCode = currentModule.moduleAccessId;
    }

    try {
      const response = await axiosInstance.put(
        `/modules/${currentModule.moduleId}/accessId`,
        data,
        { headers: { "Content-Type": "application/json" } },
      );
      if (response.status === 200) {
        await fetchModules();
        toast.success("Access ID and Feature Codes updated successfully.");
        setIsAccessIdModalOpen(false);
      }
    } catch {
      toast.error("Failed to update Access ID. Please try again.");
    }
  };

  const handleFeatureAccessCodeChange = (index, field, value) => {
    const updated = [...featureAccessCodes];
    updated[index][field] = value;
    setFeatureAccessCodes(updated);
  };

  const addFeatureAccessCode = () => {
    setFeatureAccessCodes([
      ...featureAccessCodes,
      { moduleFeatureAccessId: 0, featureAccessCode: "", featureName: "" },
    ]);
  };

  const removeFeatureAccessCode = async (index) => {
    const toRemove = featureAccessCodes[index];
    if (toRemove.moduleFeatureAccessId) {
      try {
        await axiosInstance.delete(
          `/modules/featureAccessId/${toRemove.moduleFeatureAccessId}`,
        );
      } catch {
        toast.error("Failed to delete feature access code.");
        return;
      }
    }
    setFeatureAccessCodes(featureAccessCodes.filter((_, i) => i !== index));
    toast.success("Feature access code removed.");
  };

  const handleDeleteModule = async (moduleId) => {
    const moduleToDelete = modules.find((mod) => mod.moduleId === moduleId);
    if (!moduleToDelete) return;
    if (window.confirm("Are you sure you want to delete this module?")) {
      setModules(modules.filter((mod) => mod.moduleId !== moduleId));
      setDeletedModule(moduleToDelete);
      const timeoutId = setTimeout(async () => {
        try {
          await axiosInstance.delete(`/modules/${moduleId}`);
          setDeletedModule(null);
        } catch {
          toast.error("Error deleting module.");
        }
      }, 5000);
      setUndoTimeoutId(timeoutId);
      toast.info("Module deleted. You can undo this action.");
    }
  };

  const handleUndoDelete = () => {
    if (deletedModule) {
      setModules([...modules, deletedModule]);
      clearTimeout(undoTimeoutId);
      setDeletedModule(null);
      toast.info("Module deletion undone.");
    }
  };

  const openModal = (type, module = null) => {
    setEditModule(module);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditModule(null);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg hover:border-blue-300 transition-all duration-300">
      <div className="max-w-7xl mx-auto space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Modules Management
            </h2>
            <p className="text-xs text-gray-500">
              Manage application modules and their features
            </p>
          </div>
          <div className="flex items-center gap-2">
            {deletedModule && (
              <button
                onClick={handleUndoDelete}
                className="bg-orange-100 hover:bg-orange-200 text-orange-800 text-xs font-medium py-1.5 px-3 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                  />
                </svg>
                Undo Delete
              </button>
            )}

            {/* Product badge */}
            {selectedProduct && (
              <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                <svg
                  className="w-3.5 h-3.5 text-blue-500 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                <span className="text-xs font-semibold text-blue-700">
                  {selectedProduct.productName}
                </span>
              </div>
            )}

            {/* View toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode("product")}
                className={`px-3 py-1.5 text-xs rounded-md font-medium transition-all duration-200 ${viewMode === "product" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              >
                Product View
              </button>
              <button
                onClick={() => setViewMode("all")}
                className={`px-3 py-1.5 text-xs rounded-md font-medium transition-all duration-200 ${viewMode === "all" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              >
                All Modules
              </button>
            </div>

            <button
              onClick={() => openModal()}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-1.5 px-4 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add Module
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex items-center gap-2">
              <svg
                className="animate-spin h-5 w-5 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="text-sm text-gray-500">Loading modules...</span>
            </div>
          </div>
        ) : modules.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {modules.map((module) => (
              <div
                key={module.moduleId}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md hover:border-blue-300 transition-all duration-200"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-3 py-2.5 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white truncate">
                    {module.moduleName}
                  </h3>
                  {module.moduleFeatures?.length > 0 && (
                    <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ml-1">
                      {module.moduleFeatures.length}
                    </span>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-2.5">
                  {module.moduleDescription && (
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                      {module.moduleDescription}
                    </p>
                  )}

                  {/* Features */}
                  {module.moduleFeatures?.length > 0 && (
                    <div className="mb-2">
                      <div className="grid grid-cols-2 gap-1">
                        {module.moduleFeatures.map((feature) => (
                          <div
                            key={feature.moduleFeatureId}
                            className="flex items-center gap-1 bg-gray-50 rounded px-1.5 py-1"
                          >
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />
                            <span
                              className="text-xs text-gray-700 truncate"
                              title={feature.featureName}
                            >
                              {feature.featureName}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-1.5 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => openModal("edit", module)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => handleOpenAccessIdModal(module)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                        />
                      </svg>
                      Access
                    </button>
                    <button
                      onClick={() => handleDeleteModule(module.moduleId)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <svg
              className="mx-auto h-10 w-10 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <p className="text-sm font-medium text-gray-600 mt-2">
              No modules available
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {viewMode === "product"
                ? `No modules found for ${selectedProduct?.productName || "this product"}`
                : "Get started by creating your first module"}
            </p>
          </div>
        )}

        {/* Module Form Modal */}
        {isModalOpen && (
          <Modal isOpen={isModalOpen} onClose={closeModal}>
            <ModuleForm
              type={editModule ? "edit" : "add"}
              moduleId={editModule?.moduleId}
              onClose={closeModal}
              module={editModule}
              modules={modules}
              onSuccess={fetchModules}
            />
          </Modal>
        )}

        {/* Access ID Modal */}
        {isAccessIdModalOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-3 flex items-center justify-between rounded-t-xl flex-shrink-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-white">
                    Edit Access Codes
                  </h3>
                  {currentModule && (
                    <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                      {currentModule.moduleName}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setIsAccessIdModalOpen(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {/* Module Access Code */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Module Access Code
                  </label>
                  <input
                    type="text"
                    value={moduleAccessCode}
                    onChange={(e) => setModuleAccessCode(e.target.value)}
                    placeholder="Enter Access Code"
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                {/* Feature Access Codes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Feature Access Codes
                    </label>
                    <button
                      type="button"
                      onClick={addFeatureAccessCode}
                      className="text-xs text-purple-600 hover:text-purple-700 font-medium hover:underline flex items-center gap-1"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Add Code
                    </button>
                  </div>

                  {featureAccessCodes.length > 0 ? (
                    <div className="space-y-1.5">
                      {featureAccessCodes.map((code, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-200"
                        >
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={code.featureName}
                              onChange={(e) =>
                                handleFeatureAccessCodeChange(
                                  index,
                                  "featureName",
                                  e.target.value,
                                )
                              }
                              placeholder="Feature name"
                              className="px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                            />
                            <input
                              type="text"
                              value={code.featureAccessCode}
                              onChange={(e) =>
                                handleFeatureAccessCodeChange(
                                  index,
                                  "featureAccessCode",
                                  e.target.value,
                                )
                              }
                              placeholder="Access code"
                              className="px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500 focus:border-purple-500 font-mono"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFeatureAccessCode(index)}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <p className="text-xs text-gray-400">
                        No feature access codes added yet
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0 bg-gray-50 rounded-b-xl">
                <button
                  onClick={() => setIsAccessIdModalOpen(false)}
                  className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateAccessId}
                  className="px-5 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Modules;
